import React, { useState } from 'react';
import { Calendar, ExternalLink, Newspaper, Filter } from 'lucide-react';
import SEO from '../components/SEO';
import { industryNews, NewsArticle } from '../data/industryNews';

export default function IndustryNews() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(industryNews.map(article => article.category)))];

  // Filter articles by category
  const filteredNews = selectedCategory === 'all'
    ? industryNews
    : industryNews.filter(article => article.category === selectedCategory);

  // Sort by published date (newest first)
  const sortedNews = [...filteredNews].sort((a, b) =>
    new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <SEO
        title="Gas Station Industry News - The Fuel Central"
        description="Curated industry news covering gas station market trends, regulations, technology, M&A activity, and market analysis. Stay informed with the latest developments in the fuel retail sector."
        keywords="gas station news, fuel industry updates, convenience store news, petroleum industry, market trends, regulations"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center mb-4">
                <Newspaper className="w-12 h-12 mr-3" />
                <h1 className="text-4xl md:text-5xl font-bold">Industry News</h1>
              </div>
              <p className="text-xl text-blue-100">
                Curated news and analysis from leading industry sources
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto py-4">
              <div className="flex items-center gap-3 mb-3">
                <Filter className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-semibold text-gray-700">Filter by category:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category === 'all' ? 'All News' : category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {sortedNews.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No articles found in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {sortedNews.map((article) => (
                  <article
                    key={article.id}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    {/* Featured Image */}
                    {article.imageUrl && (
                      <div className="h-48 bg-gray-200 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}

                    <div className="p-6">
                      {/* Category Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(article.publishedDate)}
                        </span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                        {article.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-gray-600 text-sm mb-4 line-clamp-4">
                        {article.excerpt}
                      </p>

                      {/* Source & Read More */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center">
                          <span className="text-xs text-gray-500">Source:</span>
                          <a
                            href={article.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-blue-600 hover:text-blue-800 ml-1"
                          >
                            {article.source}
                          </a>
                        </div>
                        <a
                          href={article.articleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          Read More
                          <ExternalLink className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Newsletter CTA */}
        <div className="bg-blue-600 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Stay Updated on Platform Development</h2>
              <p className="text-blue-100 mb-6">
                Subscribe to our newsletter for weekly updates on new features, improvements, and behind-the-scenes insights into building The Fuel Central.
              </p>
              <a
                href="/contact-us"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Subscribe to Updates
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
